import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ChatPage from './pages/ChatPage';
import CrawlPage from './pages/CrawlPage';
import SearchPage from './pages/SearchPage';
import SitesPage from './pages/SitesPage';
import SiteDetailPage from './pages/SiteDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import NotificationInfo from './pages/NotificationInfo';
import UserPreferencesPage from './pages/UserPreferencesPage';
import UserGuidePage from './pages/UserGuidePage';
import DbExplorerPage from './pages/DbExplorerPage';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardOverviewPage from './pages/dashboard/DashboardOverviewPage';
import DashboardContentPage from './pages/dashboard/DashboardContentPage';
import DashboardWidgetPage from './pages/dashboard/DashboardWidgetPage';
import DashboardBillingPage from './pages/dashboard/DashboardBillingPage';
import DashboardBookingsPage from './pages/dashboard/DashboardBookingsPage';
import DashboardSchedulePage from './pages/dashboard/DashboardSchedulePage';
import DashboardCatalogPage from './pages/dashboard/DashboardCatalogPage';
import DashboardAutomationPage from './pages/dashboard/DashboardAutomationPage';
import DashboardAdvancedPage from './pages/dashboard/DashboardAdvancedPage';
import DashboardHelpPage from './pages/dashboard/DashboardHelpPage';
import DashboardContactAdminPage from './pages/dashboard/DashboardContactAdminPage';
import DashboardSecurityPage from './pages/dashboard/DashboardSecurityPage';
import DashboardAdminPage from './pages/DashboardAdminPage';
import AdminOnlyRoute from './components/AdminOnlyRoute';
import RequireAuth from './components/RequireAuth';
import { useTheme } from './context/ThemeContext';

// Custom toast container that respects the current theme
const ToastContainer = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 7000,
        className: `toast-notification ${isDark ? 'dark' : 'light'}`,
        style: {}, // Remove inline styles to use CSS classes
        success: {
          duration: 6500,
          iconTheme: {
            primary: isDark ? '#10b981' : '#059669',
            secondary: isDark ? '#ecfdf5' : '#ffffff',
          },
          className: 'toast-notification toast-success',
        },
        error: {
          duration: 12000,
          iconTheme: {
            primary: isDark ? '#ef4444' : '#dc2626',
            secondary: isDark ? '#fef2f2' : '#ffffff',
          },
          className: 'toast-notification toast-error',
        },
      }}
    />
  );
};

function App() {
  // Add a flag to disable automatic API calls in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  const { theme } = useTheme();
  
  // Log once on initial render to help with debugging
  useEffect(() => {
    if (isDevelopment) {
      console.log('App running in development mode - some background API calls may occur due to hot reloading');
    }
    
    // Add theme class to body
    document.body.classList.toggle('theme-dark', theme === 'dark');
    document.body.classList.toggle('theme-light', theme === 'light');
    
    return () => {
      document.body.classList.remove('theme-dark', 'theme-light');
    };
  }, [isDevelopment, theme]);

  return (
    <>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Login / invite / reset — only unauthenticated entry */}
          <Route path="dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardOverviewPage />} />
            <Route path="content" element={<DashboardContentPage />} />
            <Route path="widget" element={<DashboardWidgetPage />} />
            <Route path="billing" element={<DashboardBillingPage />} />
            <Route path="bookings" element={<DashboardBookingsPage />} />
            <Route path="schedule" element={<DashboardSchedulePage />} />
            <Route path="catalog" element={<DashboardCatalogPage />} />
            <Route path="automation" element={<DashboardAutomationPage />} />
            <Route path="help" element={<DashboardHelpPage />} />
            <Route path="contact" element={<DashboardContactAdminPage />} />
            <Route path="advanced" element={<DashboardAdvancedPage />} />
            <Route path="security" element={<DashboardSecurityPage />} />
          </Route>

          {/* Everything else requires a dashboard session */}
          <Route element={<RequireAuth />}>
            <Route index element={<AdminOnlyRoute><HomePage /></AdminOnlyRoute>} />
            <Route path="chat" element={<AdminOnlyRoute><ChatPage /></AdminOnlyRoute>} />
            <Route path="crawl" element={<AdminOnlyRoute><CrawlPage /></AdminOnlyRoute>} />
            <Route path="search" element={<AdminOnlyRoute><SearchPage /></AdminOnlyRoute>} />
            <Route path="sites" element={<AdminOnlyRoute><SitesPage /></AdminOnlyRoute>} />
            <Route path="sites/:siteId" element={<AdminOnlyRoute><SiteDetailPage /></AdminOnlyRoute>} />
            <Route path="notifications" element={<AdminOnlyRoute><NotificationInfo /></AdminOnlyRoute>} />
            <Route path="preferences" element={<AdminOnlyRoute><UserPreferencesPage /></AdminOnlyRoute>} />
            <Route path="preferences/:userId" element={<AdminOnlyRoute><UserPreferencesPage /></AdminOnlyRoute>} />
            <Route path="guide" element={<AdminOnlyRoute><UserGuidePage /></AdminOnlyRoute>} />
            <Route path="db-explorer" element={<AdminOnlyRoute><DbExplorerPage /></AdminOnlyRoute>} />
            <Route path="dashboard/admin" element={<AdminOnlyRoute><DashboardAdminPage /></AdminOnlyRoute>} />
            <Route path="*" element={<AdminOnlyRoute><NotFoundPage /></AdminOnlyRoute>} />
          </Route>
        </Route>
      </Routes>
      <ToastContainer />
    </>
  );
}

export default App; 